import {
  ApiException,
  NotificationMessageViewModel,
  NotificationsApi,
  NotificationsUserSettingsInputModel,
  NotificationsUserSettingsViewModel,
  NotificationsViewModel,
  SupportRequestInputModel,
  TemplateInputModel,
  TemplateViewModel,
} from '@ail/notifications-api'
import { createApi } from '@reduxjs/toolkit/query/react'
import { USER_MANAGER } from '../../auth/userManager'
import { Constants } from '../../helpers/Constants'
import { getBaseQuery } from './apiQuery'

const client = new NotificationsApi({
  baseUrl: Constants.notificationsEndpoint,
  getToken: async () => {
    const user = await USER_MANAGER.getUser()
    return user?.access_token ?? ''
  },
})

export const notificationsApi = createApi({
  reducerPath: 'notificationsApi',
  baseQuery: getBaseQuery<ApiException>(),
  tagTypes: ['templates'],
  endpoints: (builder) => ({
    getNotifications: builder.query<
      NotificationsViewModel,
      {
        count?: number | null
        page?: number | null
        search?: string | null
        from?: string | null
        templateNames?: string[] | null
        read?: boolean | null
      }
    >({
      query: ({ count, page, search, from, templateNames, read }) =>
        client.getNotifications(count, page, search, from, templateNames, read),
    }),
    getTemplates: builder.query<TemplateViewModel[], void>({
      query: () => client.getTemplates(),
      providesTags: ['templates'],
    }),
    getTemplate: builder.query<TemplateViewModel, string>({
      query: (templateId) => client.getTemplate(templateId),
      providesTags: ['templates'],
    }),
    postTemplate: builder.mutation<TemplateViewModel, TemplateInputModel>({
      query: (model) => client.postTemplate(model),
      invalidatesTags: ['templates'],
    }),
    putTemplate: builder.mutation<TemplateViewModel, { templateId: string; model: TemplateInputModel }>({
      query: ({ templateId, model }) => client.putTemplate(templateId, model),
      invalidatesTags: ['templates'],
    }),
    deleteTemplate: builder.mutation<void, string>({
      query: (templateId) => client.deleteTemplate(templateId),
      invalidatesTags: ['templates'],
    }),
    postSupportRequest: builder.query<void, SupportRequestInputModel>({
      query: (model) => client.postSupportRequest(model),
    }),
    getUserSettings: builder.query<NotificationsUserSettingsViewModel, void>({
      query: () => client.getUserSettings(),
    }),
    putUserSettings: builder.mutation<NotificationsUserSettingsViewModel, NotificationsUserSettingsInputModel>({
      query: (model) => client.putUserSettings(model),
    }),
    getOtherUserSettings: builder.query<NotificationsUserSettingsViewModel, string>({
      query: (id) => client.getOtherUserSettings(id),
    }),
    getNotificationMessages: builder.query<NotificationMessageViewModel[], void>({
      query: () => client.getNotificationMessages(),
    }),
    putOtherUserSettings: builder.mutation<
      NotificationsUserSettingsViewModel,
      {
        id: string
        model: NotificationsUserSettingsInputModel
      }
    >({
      query: ({ id, model }) => client.putOtherUserSettings(id, model),
    }),
  }),
})

export const {
  useGetNotificationsQuery,
  useGetUserSettingsQuery,
  usePutUserSettingsMutation,
  useGetOtherUserSettingsQuery,
  usePutOtherUserSettingsMutation,
  useLazyGetNotificationsQuery,
  useGetTemplatesQuery,
  useGetTemplateQuery,
  usePostTemplateMutation,
  usePutTemplateMutation,
  useDeleteTemplateMutation,
  useLazyPostSupportRequestQuery,
  useGetNotificationMessagesQuery,
} = notificationsApi
